import React, { useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';

import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { trackAuthEvent } from '../../services/analytics';
import { brand } from '../../design-system/theme/tokens';

const PAYWALL_TRIGGER = 'results_lock';

const COLORS = {
  primary: brand.primary,
  primaryLight: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

interface TrialResultsLockProps {
  overallLevel?: string;
}

export default function TrialResultsLock({ overallLevel }: TrialResultsLockProps) {
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    trackAuthEvent('trial_paywall_viewed', { trigger: PAYWALL_TRIGGER, overall_level: overallLevel ?? null });
  }, [overallLevel]);

  const handleSubscribe = () => {
    trackAuthEvent('trial_paywall_cta_clicked', { trigger: PAYWALL_TRIGGER, destination: 'gated_plan_selection' });
    router.push(ROUTES.GATED_PLAN_SELECTION);
  };

  return (
    <View style={styles.overlay}>
      <View style={styles.backdrop} />
      <View style={styles.content}>
        <View className="items-center rounded-2xl bg-white overflow-hidden" style={{ maxWidth: 380, width: '100%', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 8 }}>
          {/* Gradient header */}
          <LinearGradient
            colors={[COLORS.primary, COLORS.primaryLight]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ width: '100%', paddingVertical: 16, paddingHorizontal: 24, alignItems: 'center' }}
          >
            <View className="flex-row items-center">
              <MaterialIcons name="lock" size={18} color="white" />
              <Text className="text-white font-semibold ml-2 text-sm">
                {t('trial.expired.lockBadge') || 'Preview Ended'}
              </Text>
            </View>
          </LinearGradient>

          <View className="px-6 py-6 items-center">
            {/* Level teaser */}
            {overallLevel && (
              <View className="flex-row items-center px-5 py-2.5 rounded-full mb-4" style={{ backgroundColor: `${COLORS.warning}15`, borderWidth: 1, borderColor: `${COLORS.warning}30` }}>
                <MaterialIcons name="emoji-events" size={20} color={COLORS.warning} />
                <Text className="ml-2 font-bold text-base" style={{ color: COLORS.warning }}>
                  {t('trial.expired.yourLevel') || 'Your level:'} {overallLevel}
                </Text>
              </View>
            )}

            <Text className="text-xl font-bold text-center mb-2" style={{ color: COLORS.text.primary }}>
              {t('trial.expired.title') || 'Your analysis results are locked'}
            </Text>

            <Text className="text-sm text-center leading-5 mb-5" style={{ color: COLORS.text.secondary }}>
              {t('trial.expired.description') ||
                'Subscribe to unlock your full results forever and keep improving with unlimited analyses.'}
            </Text>

            {/* What they unlock */}
            <View className="w-full rounded-xl p-4 mb-5" style={{ backgroundColor: '#F8FAFC' }}>
              <Text className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: COLORS.text.muted }}>
                {t('trial.expired.unlockTitle') || 'Unlock with subscription'}
              </Text>
              {[
                { icon: 'lock-open' as const, text: t('trial.expired.unlock1') || 'This analysis — permanently yours' },
                { icon: 'all-inclusive' as const, text: t('trial.expired.unlock2') || 'Unlimited new analyses' },
                { icon: 'chat' as const, text: t('trial.expired.unlock3') || 'Unlimited AI coaching conversations' },
                { icon: 'trending-up' as const, text: t('trial.expired.unlock4') || 'Track progress across sessions' },
              ].map((item, idx) => (
                <View key={idx} className="flex-row items-center mb-2">
                  <View
                    className="w-6 h-6 rounded-full items-center justify-center mr-2"
                    style={{ backgroundColor: `${COLORS.success}15` }}
                  >
                    <MaterialIcons name={item.icon} size={14} color={COLORS.success} />
                  </View>
                  <Text className="text-sm flex-1" style={{ color: COLORS.text.secondary }}>{item.text}</Text>
                </View>
              ))}
            </View>

            {/* Launch offer */}
            <View className="mb-3 rounded-lg px-3 py-2" style={{ backgroundColor: '#FEF3C7', borderWidth: 1, borderColor: '#FCD34D' }}>
              <Text className="text-xs font-semibold text-center" style={{ color: '#92400E' }}>
                {`🎉 ${t('trial.launchOffer') || 'Launch offer: 20% off your first 3 months — use code LAUNCH20 at checkout'}`}
              </Text>
            </View>

            {/* CTA */}
            <TouchableOpacity onPress={handleSubscribe} className="w-full rounded-xl overflow-hidden mb-1" activeOpacity={0.85}>
              <LinearGradient
                colors={[COLORS.primary, '#2563EB']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{ paddingVertical: 15, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
              >
                <MaterialIcons name="lock-open" size={20} color="white" />
                <Text className="text-white font-bold ml-2 text-base">
                  {t('trial.expired.subscribeCTA') || 'Unlock My Results'}
                </Text>
              </LinearGradient>
            </TouchableOpacity>

            <Text className="text-xs text-center mt-1" style={{ color: COLORS.text.muted }}>
              {t('trial.expired.guarantee') || 'Cancel anytime. Results stay forever.'}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 100,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
});
